import React, { useEffect } from "react";
import AOS from "aos";
import { motion } from "framer-motion";
import Hero from "../components/Hero";
import SEO from "../components/SEO";
import {
    Eye,
    Target,
    Award,
    BookOpen,
    Globe,
    Layers,
    Sun,
    ChevronRight,
} from "lucide-react";

const WhoWeAre = () => {
    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    const heroSlides = [
        {
            id: 1,
            image: "/assets/img/prog-1.jpg",
            title: "Who We Are",
            subtitle:
                "Empowering students to Grow, Prosper, and Excel in all their aspirations.",
            buttonText: "Learn More",
            buttonLink: "#about-section",
        },
    ];

    return (
        <section className="bg-gray-50 min-h-screen pt-1">
            <SEO 
                title="Who We Are — Africa Research University" 
                description="Discover ARU's mission, vision, and values. Empowering students to grow, prosper, and excel."
                url="/who-we-are"
                breadcrumbs={[
                    { name: 'Home', url: 'https://africaresearchuniversity.com' },
                    { name: 'About', url: 'https://africaresearchuniversity.com/about' },
                    { name: 'Who We Are', url: 'https://africaresearchuniversity.com/who-we-are' }
                ]}
            />
            {/* Hero Section */}
            <Hero
                slides={heroSlides}
                interval={5000}
                height="350px"
                borderRadius="50px"
                hidden={true}
            />

            {/* About Content */}
            <div id="about-section" className="container mx-auto px-6 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-5xl mx-auto text-gray-800 space-y-16"
                >
                    {/* Vision */}
                    <section
                        className="flex flex-col sm:flex-row items-center gap-8"
                        data-aos="fade-up"
                    >
                        <div className="flex-shrink-0 bg-green-100 text-green-700 p-5 rounded-2xl">
                            <Eye size={48} />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-green-700 mb-3">Vision</h2>
                            <p className="text-lg italic font-medium mb-4">
                                “Our vision is to enable our students to Grow, Prosper and Excel
                                in whatever they aspire to achieve.”
                            </p>
                            <p className="leading-relaxed text-gray-700">
                                We create educational opportunities for personal growth,
                                prosperity, and excellence, certifying applicable competencies.
                                Both universities emphasize managerial skills and knowledge and
                                devote two semester courses to research—preparing students not
                                only for postgraduate research but also to contribute meaningfully
                                in the workplace.
                            </p>
                        </div>
                    </section>

                    {/* Mission */}
                    <section
                        className="flex flex-col sm:flex-row items-center gap-8"
                        data-aos="fade-up"
                    >
                        <div className="flex-shrink-0 bg-green-100 text-green-700 p-5 rounded-2xl">
                            <Target size={48} />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-green-700 mb-3">
                                Mission Statement
                            </h2>
                            <p className="leading-relaxed text-gray-700">
                                We provide our students with flexible learning opportunities
                                through modern technology, world-class materials, and personal
                                guidance.
                            </p>
                        </div>
                    </section>

                    {/* Motto */}
                    <section
                        className="flex flex-col sm:flex-row items-center gap-8"
                        data-aos="fade-up"
                    >
                        <div className="flex-shrink-0 bg-green-100 text-green-700 p-5 rounded-2xl">
                            <Award size={48} />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-green-700 mb-3">
                                Motto — Grow, Prosper, Excel
                            </h2>
                            <p className="leading-relaxed text-gray-700 mb-4">
                                We create educational opportunities for personal growth,
                                prosperity, and excellence, certifying applicable competencies.
                                Both universities emphasize managerial skills and devote two
                                semester courses to research—preparing students for postgraduate
                                study and workplace impact.
                            </p>
                            <p className="leading-relaxed text-gray-700">
                                Through these values, we guide our students, staff, and
                                stakeholders in their journey of achievement:
                            </p>
                            <ul className="mt-3 text-gray-700 space-y-3">
                                <li className="flex items-start">
                                    <ChevronRight className="text-green-600 mt-1" />
                                    <span>
                                        <strong>Grow:</strong> Through exposure to academic
                                        excellence, nurturing human potential.
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <ChevronRight className="text-green-600 mt-1" />
                                    <span>
                                        <strong>Prosper:</strong> Through growth comes prosperity—
                                        material, spiritual, and mental.
                                    </span>
                                </li>
                                <li className="flex items-start">
                                    <ChevronRight className="text-green-600 mt-1" />
                                    <span>
                                        <strong>Excel:</strong> Achieving excellence in careers,
                                        businesses, and personal lives—becoming global leaders.
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </section>

                    {/* Logos */}
                    <section
                        className="flex flex-col items-center gap-10"
                        data-aos="fade-up"
                    >
                        <h2 className="text-3xl font-bold text-green-700 text-center">
                            Logos & Symbolism
                        </h2>

                        {/* Logo Image */}
                        <img
                            src="/assets/img/arulogo.png"
                            alt="University Logo"
                            className="w-68 h-48 mx-auto mb-8 drop-shadow-md"
                        />

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
                            <div className="bg-white shadow rounded-xl p-6 text-center hover:shadow-md transition">
                                <Globe className="mx-auto mb-3 text-green-600" size={40} />
                                <h3 className="text-xl font-semibold mb-2">
                                    Africa Continent
                                </h3>
                                <p className="text-gray-700 text-sm leading-relaxed">
                                    Represents our African roots and mission to serve its people,
                                    symbolizing belief in research and innovation as tools for
                                    continental development.
                                </p>
                            </div>

                            <div className="bg-white shadow rounded-xl p-6 text-center hover:shadow-md transition">
                                <BookOpen className="mx-auto mb-3 text-green-600" size={40} />
                                <h3 className="text-xl font-semibold mb-2">Open Book</h3>
                                <p className="text-gray-700 text-sm leading-relaxed">
                                    Symbolizes lifelong learning and wisdom. The rising sun, laureate
                                    leaves, and eagle wings resting upon it embody strength and
                                    enlightenment.
                                </p>
                            </div>

                            <div className="bg-white shadow rounded-xl p-6 text-center hover:shadow-md transition">
                                <Layers className="mx-auto mb-3 text-green-600" size={40} />
                                <h3 className="text-xl font-semibold mb-2">
                                    The Arch (Keystone)
                                </h3>
                                <p className="text-gray-700 text-sm leading-relaxed">
                                    Symbolizes unity, faith, and stability—the keystone that holds
                                    our foundation together, representing moral and academic
                                    integrity.
                                </p>
                            </div>
                        </div>
                    </section>
                    <section
                        className="flex flex-col items-center gap-10"
                        data-aos="fade-up"
                    >
                        <h2 className="text-3xl font-bold text-green-700 text-center">
                            ARU Organogram
                        </h2>
                        {/* Logo Image */}
                        <img
                            src="/assets/uploads/aru-orga.webp"
                            alt="University Organogram"
                            className="w-full h-auto mx-auto mb-8"
                        />
                    </section>
                </motion.div>
            </div>
        </section>
    );
};

export default WhoWeAre;
